import Vue from 'vue';
import { getContentArea } from '@/contentful/index';
import {
    dateFormat,
    dateFormatYYYYMMDD,
  } from '@/constants';
import store from '@/store';
import { isNil } from 'lodash';
import {
    MemberAccountResponseModel,
    AccountStatementsResponseModel,
    AccountStatementDetail,
    PdfMakeTableCell,
    TableHeader,
    MemberPersonalInformationResponseModel,
    MemberCustomerServiceInfoResponseModel,
    UserModel,
  } from '@/@types/member';
import moment from 'moment';
import markdownIt from 'markdown-it/dist/markdown-it.min.js';
import htmlToPdfmake from 'html-to-pdfmake';
import { has } from 'lodash';
import GeneratePDFService from '@/services/GeneratePDF.service';



interface Data {
    APIDateFormat: string;
    pdfLogo: string;
    claimsData: PdfMakeTableCell[][];
    content: string;
    reportPeriod: string;
    taxYear: string;
    pdfTagline: string;
}
export default Vue.extend({
    data(): Data {
        return {
          APIDateFormat: dateFormatYYYYMMDD,
          pdfLogo: '',
          claimsData: [],
          content: '',
          reportPeriod: '',
          taxYear: '',
          pdfTagline: '',
        };
      },
    beforeMount() {
        // Generate base64 logo for PDF generation
        const CANVAS = document.createElement('CANVAS') as HTMLCanvasElement;
        const CTX: CanvasRenderingContext2D = CANVAS.getContext('2d')!;
        const IMG = document.createElement('img');
        try {
          // use pdf-specific logo if present
          IMG.src = require('@/further-branding-module/static/logo_pdf.svg');
        } catch (error) {
          // fall back to default logo used elsewhere
          IMG.src = require('@/further-branding-module/static/logo_dark.svg');
        }
        IMG.onload = () => {
            CANVAS.width = IMG.width;
            CANVAS.height = IMG.height;
            CTX.drawImage(IMG, 0, 0);
            this.pdfLogo = CANVAS.toDataURL();
        };
        this.$store.dispatch('getPersonalInformation');
        this.$store.dispatch('getMemberCustomerServiceInformation');
        this.getContent();
        this.getTagline();
    },
    computed: {
        memberAccountStatements(): AccountStatementsResponseModel {
            return this.$store.getters.memberAccountStatements ? this.$store.getters.memberAccountStatements : {};
          },
        account(): MemberAccountResponseModel {
            return this.$store.getters.currentAccount(this.$route.params.id);
        },
        user(): UserModel {
            return this.$store.getters.user;
        },
        personalInfo(): MemberPersonalInformationResponseModel {
            return this.$store.getters.personalInformation;
        },
        customerServiceInfo(): MemberCustomerServiceInfoResponseModel {
            return this.$store.getters.customerServiceInformation;
        },

        header(): any[] {
            return [
                {
                    text: this.$options.filters!.t('pdf-statement-title'),
                    style: 'header1',
                    alignment: 'left',
                },
                {
                    columns: [
                    [
                        { text: `${this.user.firstName} ${this.user.lastName}`, style: 'addressee'},
                        { text: this.personalInfo.address.addressLine1, style: 'addressee'},
                        this.personalInfo.address.addressLine2 ? {text: this.personalInfo.address.addressLine2, style: 'addressee'} : null,
                        { text: `${this.personalInfo.address.city}, ${this.personalInfo.address.state} ${this.personalInfo.address.zipCode}`, style: 'addressee'},
                    ],
                    {
                        text: ' ', // filler
                        width: 50,
                    },
                    [
                        {text: [{ text: `${this.$options.filters!.t('pdf-statement-employer')} `, bold: true}, { text: this.user.groupName}]},
                        {text: [{ text: `${this.$options.filters!.t('pdf-statement-sa-id')} `, bold: true}, { text: this.user.selectAccountId}]},
                        {text: [{ text: `${this.$options.filters!.t('pdf-statement-date')} `, bold: true}, { text: moment().format('MM/DD/YYYY')}]},
                    ],
                    ],
                    margin: [0, 5, 0, 5],
                },
                {
                    text: [
                    {text: this.$options.filters.varReplace(this.$options.filters!.t('pdf-statement-thank-you-message'), {groupBrandName: this.customerServiceInfo.brandName})},
                    this.reportPeriod === 'tax-year' ?
                        {text: this.$options.filters.varReplace(this.$options.filters!.t('pdf-statement-tax-year-message'), {taxYear: this.taxYear})}
                        : {text: this.$options.filters.varReplace(this.$options.filters!.t('pdf-statement-range-message'), {startDate: moment(this.memberAccountStatements.reportStartDate).format(dateFormat), endDate: moment(this.memberAccountStatements.reportEndDate).format(dateFormat)})},
                    {text: this.$options.filters.varReplace(this.$options.filters!.t('pdf-statement-visit-our-website-message'), {groupMainSiteURL: this.customerServiceInfo.url})},
                    ],
                    margin: [0, 5, 0, 0],
                },
            ];
        },

        summary(): any[] {
            const summary = [];
            summary.push({
                text: this.$options.filters.varReplace(this.$options.filters!.t('pdf-statement-product-summary'), {product: store.getters.currentAccount(this.$route.params.id).productType}),
                style: 'header2',
                alignment: 'left',
            });
            if (this.account.productType !== 'HSA' && this.account.productType !== 'VEBA') {
                summary.push({
                    columns: [
                    [
                        !isNil(this.account.startDate) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-account-start-date')},
                            {text: moment(this.account.startDate).format(dateFormat), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.account.endDate) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-account-end-date')},
                            {text: moment(this.account.endDate).format(dateFormat), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.account.graceEndDate) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-grace-period-end')},
                            {text: moment(this.account.graceEndDate).format(dateFormat), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.account.claimFilingDeadline) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-claims-filing-deadline')},
                            {text: moment(this.account.claimFilingDeadline).format(dateFormat), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.account.termDate) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-account-termination-date')},
                            {text: moment(this.account.termDate).format(dateFormat), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.threshold) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-threshold-amount')},
                            {text: this.$options.filters.currency(this.memberAccountStatements.threshold), alignment: 'right'},
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.accumulation) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-accumulation-amount')},
                            {text: this.$options.filters.currency(this.memberAccountStatements.accumulation), alignment: 'right' },
                        ],
                        } : null,
                    ],
                    {
                        text: '',
                        width: 30,
                    },
                    [
                        !isNil(this.memberAccountStatements.rollover) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-rollover-prior-year')},
                            {text: this.$options.filters.currency(this.memberAccountStatements.rollover), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.election) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-election-amount')},
                            {text: this.$options.filters.currency(this.memberAccountStatements.election), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.employerFunding) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-employer-funding')},
                            {text: this.$options.filters.currency(this.memberAccountStatements.employerFunding), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.contributions) && this.memberAccountStatements.contributions.amount ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-contributions')},
                            {text: this.$options.filters.currency(this.memberAccountStatements.contributions.amount), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.claims) && this.memberAccountStatements.claims.amount ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-withdrawals')},
                            {text: this.$options.filters.currency(this.memberAccountStatements.claims.amount), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.availableBalance) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-available-base-balance')},
                            {text: this.memberAccountStatements.availableBalance >= 9999999.99
                            ? this.$options.filters.t('statements_label_n/a')
                            : this.$options.filters.currency(this.memberAccountStatements.availableBalance), alignment: 'right' },
                        ],
                        } : null,
                    ],
                    ],
                });
            } else {
                summary.push({
                    columns: [
                    [
                        !isNil(this.account.startDate) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-account-effective-date')},
                            {text: moment(this.account.startDate).format(dateFormat), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.account.claimFilingDeadline) ? {
                        columns: [
                            {text: this.$options.filters!.t('pdf-statement-claims-filing-deadline')},
                            {text: moment(this.account.claimFilingDeadline).format(dateFormat), alignment: 'right' },
                        ],
                        } : null,
                        !isNil(this.memberAccountStatements.beginingBalance) ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-beginning-balance')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.beginingBalance), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.contributions) && this.memberAccountStatements.contributions.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-contributions')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.contributions.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.currentTaxYearContributions) && this.memberAccountStatements.currentTaxYearContributions.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-contributions-current-tax-year')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.currentTaxYearContributions.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.priorTaxYearContributions) && this.memberAccountStatements.priorTaxYearContributions.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-contributions-prior-tax-year')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.priorTaxYearContributions.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.investmentTransfer)  && this.memberAccountStatements.investmentTransfer.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-investment-account-transfers')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.investmentTransfer.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.otherTransfers) && this.memberAccountStatements.otherTransfers.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-other-transfers')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.otherTransfers.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.interest) && this.memberAccountStatements.interest.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-interest-earned')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.interest.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.claims) && this.memberAccountStatements.claims.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-withdrawals')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.claims.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.fees) && this.memberAccountStatements.fees.amount ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-fees')},
                                {text: this.$options.filters.currency(this.memberAccountStatements.fees.amount), alignment: 'right'},
                            ],
                        } : null,
                        !isNil(this.memberAccountStatements.availableBalance) ? {
                            columns: [
                                {text: this.$options.filters!.t('pdf-statement-available-base-balance')},
                                {text: this.memberAccountStatements.availableBalance >= 9999999.99
                                    ? this.$options.filters.t('statements_label_n/a')
                                    : this.$options.filters.currency(this.memberAccountStatements.availableBalance), alignment: 'right' },
                            ],
                        } : null,
                    ],
                    [],
                    ],
                });
            }
            return summary;
        },

        claimsHeaders(): TableHeader[] {
            return [
            {
                text: this.$options.filters!.t('pdf-transaction-detail-service-date'),
                value: 'serviceDate',
                align: 'left',
            },
            {
                text: this.$options.filters!.t('pdf-transaction-detail-claim-details'),
                value: 'claimDetails',
                align: 'left',
            },
            {
                text: this.$options.filters!.t('pdf-transaction-detail-paid-amount'),
                value: 'paidAmount',
                align: 'right',
            },
            {
                text: this.$options.filters!.t('pdf-transaction-detail-denied-amount'),
                value: 'deniedAmount',
                align: 'right',
            },
            {
                text: this.$options.filters!.t('pdf-transaction-detail-denied-reason'),
                value: 'deniedReason',
                align: 'left',
            },
            {
                text: this.$options.filters!.t('pdf-transaction-detail-process-date'),
                value: 'processDate',
                align: 'left',
            },
            ];
        },

        claims(): any[] {
            if (
                (this.account.productType !== 'HSA' && this.account.productType !== 'VEBA')
                && has(this.memberAccountStatements, 'claims.details')
            ) {
                const claimRows: [any] = [
                    {
                        text: this.memberAccountStatements.claims.details ? this.$options.filters!.t('pdf-transactions-header') : '',
                        style: 'header2',
                    },
                ];
                this.memberAccountStatements.claims.details.forEach( (claim: AccountStatementDetail) => {
                    const claimColumns = [];
                    const filler = {
                        text: '',
                        width: 20,
                    };
                    let claimColumn: Array<{ text: string, bold?: string, alignment?: string, margin?: object}> = [
                        {
                            text: (!isNil(claim.endDate) &&  claim.endDate !== claim.startDate) ? this.$options.filters!.t('pdf-transaction-detail-service-dates') : this.$options.filters!.t('pdf-transaction-detail-service-date'),
                            bold: 'true',
                        },
                        {
                            text: (!isNil(claim.endDate) &&  claim.endDate !== claim.startDate) ? `${moment(claim.startDate).format(dateFormat)} - ${moment(claim.endDate).format(dateFormat)}` : moment(claim.startDate).format(dateFormat),
                        },
                    ];
                    claimColumns.push(claimColumn, filler);
                    claimColumn = [
                        {
                            text: this.$options.filters!.t('pdf-transaction-detail-claim-details'),
                            bold: 'true',
                        },
                        {
                            text: claim.transactions,
                        },
                        {
                            text: claim.providerName,
                        },
                        {
                            text: claim.patientName,
                        },
                        {
                            text: `${this.$options.filters!.t('pdf-transaction-detail-claim-number')}: ${claim.claimNumber}`,
                        },
                    ];
                    claimColumns.push(claimColumn, filler);
                    claimColumn = [
                        {
                            text: this.$options.filters!.t('pdf-transaction-detail-paid-amount'),
                            bold: 'true',
                            alignment: 'right',
                        },
                        {
                            text: this.$options.filters.currency(claim.amount),
                            alignment: 'right',
                        },
                        {
                            text: claim.deniedAmount ? `${this.$options.filters!.t('pdf-transaction-detail-denial-reason')}: ${claim.denialReason}` : null,
                            alignment: 'right',
                        },
                    ];
                    claimColumns.push(claimColumn, filler);
                    claimColumn = [
                        {
                            text: this.$options.filters!.t('pdf-transaction-detail-denied-amount'),
                            bold: 'true',
                            alignment: 'right',
                            margin: [0, 0, 10, 0],
                        },
                        {
                            text: this.$options.filters.currency(claim.deniedAmount ? claim.deniedAmount : 0),
                            alignment: 'right',
                            margin: [0, 0, 10, 0],
                        },
                    ];
                    claimColumns.push(claimColumn, filler);
                    claimColumn = [
                        {
                            text: this.$options.filters!.t('pdf-transaction-detail-process-date'),
                            bold: 'true',
                        },
                        {
                            text: moment(claim.postedDate).format(dateFormat),
                        },
                    ];
                    claimColumns.push(claimColumn);

                    claimRows.push({
                        columns: claimColumns,
                        margin: [0, 3, 0, 9],
                        unbreakable: true,
                    });
                });
                return claimRows;
            } else {
                return null;
            }
        },

        notes(): any[] {
            const noteItems = [];
            // non HSA and VEBA
            if (this.account.productType !== 'HSA' && this.account.productType !== 'VEBA') {
                noteItems.push(
                    {
                        text: this.$options.filters!.t('pdf-statement-please-note'),
                        style: 'header2',
                    },
                );
                noteItems.push(
                    {
                        text: this.memberAccountStatements.graceEndDate ?
                            this.$options.filters!.t('pdf-statement-claims-grace-period') :
                            this.$options.filters!.t('pdf-statement-claims-no-grace'),
                    },
                );
                noteItems.push(
                    {
                        text: this.$options.filters!.t('pdf-statement-hra-or-fsa-note'),
                    },
                );
            // HSA and VEBA
            } else if (this.account.productType === 'HSA' || this.account.productType === 'VEBA') {
                let overInvestmentThreshold;
                if (this.account.investmentThreshold) {
                    if (this.account.investmentBalance > this.account.investmentThreshold) {
                        overInvestmentThreshold = true;
                    } else {
                        overInvestmentThreshold = false;
                    }
                }

                if (!isNil(this.account.hasInvestmentAccount) || !isNil(overInvestmentThreshold)) {
                    noteItems.push(
                        {
                            text: this.$options.filters!.t('pdf-statement-please-note'),
                            style: 'header2',
                        },
                    );
                }
                if (this.account.hasInvestmentAccount) {
                    noteItems.push(
                        {
                            text: this.$options.filters!.t('pdf-statement-investment-message'),
                            margin: [10, 0, 0, 0],
                        },
                    );
                }
                if (!isNil(overInvestmentThreshold) && overInvestmentThreshold && !this.account.hasInvestmentAccount) {
                    noteItems.push(
                        {
                            text: this.$options.filters!.varReplace(this.$options.filters!.t('pdf-statement-can-open-investment-account'), { threshold: this.account.investmentThreshold }),
                            margin: [10, 0, 0, 0],
                        },
                    );
                }
                if (!isNil(overInvestmentThreshold) && !overInvestmentThreshold && !this.account.hasInvestmentAccount) {
                    noteItems.push(
                        {
                            text: this.$options.filters!.varReplace(this.$options.filters!.t('pdf-statement-cannot-open-investment-account'), { threshold: this.account.investmentThreshold }),
                            margin: [10, 0, 0, 0],
                        },
                    );
                }
            }
            return noteItems;
        },

        footer(): any[] {
            return [
                {
                    text: this.$options.filters!.t('pdf-footer-visit-website'),
                    style: 'header2',
                },
                {
                    text: this.$options.filters!.t('pdf-footer-visit-website-maximize-benefits'),
                    margin: [10, 0, 0, 0],
                },
                {
                    text: this.$options.filters!.t('pdf-footer-visit-website-view-transactions'),
                    margin: [10, 0, 0, 0],
                },
                {
                    text: this.$options.filters!.t('pdf-footer-visit-website-setup-direct-deposit'),
                    margin: [10, 0, 0, 0],
                },
                {
                    text: this.$options.filters!.t('pdf-footer-notify-if-not-correct'),
                    margin: [0, 10, 0, 10],
                },
                {
                    text: this.$options.filters.phone(this.customerServiceInfo.phoneNumber),
                    margin: [10, 0, 0, 0],
                },
                {
                    text: this.customerServiceInfo.hours,
                    margin: [10, 0, 0, 10],
                },
                ...htmlToPdfmake(
                    this.content,
                    {
                      defaultStyles: {
                        h1: {
                          fontSize: 15,
                        },
                      },
                    },
                ),
            ];
        },

    },
    methods: {
        async createPdf() {
            GeneratePDFService
            .createPdf({
                pageOrientation: 'portrait',
                header: (currentPage, pageCount) => {
                    return [
                        {
                            image: this.pdfLogo,
                            width: 150,
                            margin: [60, 60, 0, 10],
                            alignment: 'left',
                        },
                        (currentPage === 1 && this.pdfTagline.length) ?
                            {
                            text: this.pdfTagline,
                            margin: [60, -5, 0, 0],
                            fontSize: 6,
                            alignment: 'left',
                            } : null,
                    ];
                },
                content: [
                    this.header,
                    {
                        stack: this.summary,
                        margin: [0, 0, 0, 10],
                    },
                    this.claims,
                    {
                        stack: this.notes,
                        unbreakable: true,
                    },
                    {
                        stack: this.footer,
                        unbreakable: true,
                    },
                ],
                styles: {
                    header1: {
                        bold: true,
                        fontSize: 15,
                        margin: [0, 10, 0, 8],
                    },
                    header2: {
                        bold: true,
                        fontSize: 10,
                        margin: [0, 20, 0, 10],
                    },
                    addressee: {
                        fontSize: 9,
                    },
                },
                defaultStyle: {
                    fontSize: 8.5,
                    color: '#222',
                    lineHeight: 1.3,
                },
                pageMargins: [ 60, 122, 60, 60 ],
                pageSize: 'LETTER',
            },
            'statement');
        },
        getContent(): void {
            getContentArea('service-mark')
                .then((data) => {
                if (data) {
                    const md = new markdownIt();
                    this.content = md.render(data.fields.content);
                }
            });
        },
        getTagline(): void {
            getContentArea('pdf-tagline')
                .then((data) => {
                if (data) {
                    this.pdfTagline = data.fields.content;
                }
            });
        },
    },
});

